import { Search, FileText, HardDrive, Wrench, Settings, Headphones } from "lucide-react";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";

const steps = [
  { icon: Search, number: "01", title: "Site Assessment", description: "Evaluate location & electrical capacity" },
  { icon: FileText, number: "02", title: "Permits & Rebates", description: "Handle paperwork & identify incentives" },
  { icon: HardDrive, number: "03", title: "Hardware", description: "Procure certified AC/DC equipment" },
  { icon: Wrench, number: "04", title: "Installation", description: "Professional certified installation" },
  { icon: Settings, number: "05", title: "Configuration", description: "Network setup & integrations" },
  { icon: Headphones, number: "06", title: "Support", description: "24/7 monitoring & maintenance" },
];

export function ProcessSectionNew() {
  return (
    <section className="py-28 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-card/30" />
      <div className="absolute top-0 left-0 w-full section-divider" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <ScrollReveal className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold">
            Turnkey <span className="text-gradient">Deployment</span>
          </h2>
          <p className="text-muted-foreground text-lg mt-4 max-w-xl mx-auto">
            From planning to operation—we handle everything so you focus on your business.
          </p>
        </ScrollReveal>

        {/* Timeline */}
        <div className="relative max-w-5xl mx-auto">
          {/* Connection Line */}
          <motion.div
            className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent hidden lg:block"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.3 }}
          />

          {/* Steps */}
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" staggerDelay={0.1}>
            {steps.map((step, idx) => (
              <StaggerItem key={idx}>
                <motion.div className="relative group" whileHover={{ y: -8 }} transition={{ duration: 0.2 }}>
                  {/* Node */}
                  <div className="flex flex-col items-center text-center">
                    <motion.div
                      className="w-16 h-16 rounded-2xl bg-card border border-border/50 flex items-center justify-center mb-4 group-hover:border-primary/50 group-hover:bg-primary/10 transition-all relative z-10"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <step.icon className="h-7 w-7 text-primary" />
                    </motion.div>
                    <span className="text-xs text-primary/60 font-mono mb-1">{step.number}</span>
                    <h3 className="font-semibold text-sm mb-1">{step.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>

        {/* Benefits Row */}
        <ScrollReveal delay={0.4}>
          <div className="mt-20 grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { value: "Low", label: "Upfront CAPEX Options" },
              { value: "Single", label: "Point of Contact" },
              { value: "98.5%", label: "Uptime SLA" },
            ].map((benefit, idx) => (
              <motion.div
                key={idx}
                className="text-center p-6 bg-card/50 border border-border/50 rounded-2xl"
                whileHover={{ scale: 1.03, borderColor: "hsl(var(--primary) / 0.3)" }}
              >
                <div className="text-3xl font-bold text-primary mb-1">{benefit.value}</div>
                <div className="text-sm text-muted-foreground">{benefit.label}</div>
              </motion.div>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
